import { FormEvent, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  createGateLog,
  deleteGateLog,
  updateGateLog,
} from '../../api/gateLog';
import {
  CompanyGateLog,
  GATE_LOG_TYPE_OPTIONS,
  GateLogType,
  formatGateLogTime,
} from '../../types/gateLog';
import { notifyError } from '../../utils/notify';
import './CalendarEventModal.css';
import '../mypage/GateLogPanel.css';

interface Props {
  mode: 'create' | 'edit';
  date: string;
  log?: CompanyGateLog | null;
  onClose: () => void;
  onSaved: () => void;
}

function formatDateLabel(iso: string) {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  const wd = ['일', '월', '화', '수', '목', '금', '토'][new Date(y, m - 1, d).getDay()];
  return `${y}년 ${m}월 ${d}일 (${wd})`;
}

function GateLogModal({ mode, date, log, onClose, onSaved }: Props) {
  const [logDate, setLogDate] = useState(date);
  const [logTime, setLogTime] = useState('');
  const [logType, setLogType] = useState<GateLogType>('VISIT');
  const [title, setTitle] = useState('');
  const [partyName, setPartyName] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [trackingNo, setTrackingNo] = useState('');
  const [memo, setMemo] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLogDate(log?.logDate?.slice(0, 10) || date);
    setLogTime(formatGateLogTime(log?.logTime));
    setLogType((log?.logType as GateLogType) || 'VISIT');
    setTitle(log?.title || '');
    setPartyName(log?.partyName || '');
    setContactName(log?.contactName || '');
    setContactPhone(log?.contactPhone || '');
    setTrackingNo(log?.trackingNo || '');
    setMemo(log?.memo || '');
  }, [date, log]);

  const isParcel = logType === 'PARCEL_IN' || logType === 'PARCEL_OUT';
  const isVisit = logType === 'VISIT';

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      notifyError('내용(방문자명·요약)을 입력해 주세요.');
      return;
    }
    try {
      setSaving(true);
      const payload = {
        logDate,
        logTime: logTime.trim() || undefined,
        logType,
        title: title.trim(),
        partyName: partyName.trim() || undefined,
        contactName: contactName.trim() || undefined,
        contactPhone: contactPhone.trim() || undefined,
        trackingNo: trackingNo.trim() || undefined,
        memo: memo.trim() || undefined,
      };
      if (mode === 'edit' && log) {
        await updateGateLog(log.logId, payload);
      } else {
        await createGateLog(payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      notifyError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '저장에 실패했습니다.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!log || !window.confirm('이 기록을 삭제할까요?')) return;
    try {
      setSaving(true);
      await deleteGateLog(log.logId);
      onSaved();
      onClose();
    } catch {
      notifyError('삭제에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="cal-event-modal__backdrop" role="presentation" onClick={onClose}>
      <form
        className="cal-event-modal cal-event-modal--gate"
        role="dialog"
        aria-modal="true"
        aria-labelledby="gate-log-modal-title"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => void handleSubmit(e)}
      >
        <div className="cal-event-modal__head">
          <div>
            <p className="cal-event-modal__eyebrow">GATE LOG</p>
            <h3 id="gate-log-modal-title">
              {mode === 'edit' ? '출입·수발신 수정' : '출입·수발신 등록'}
            </h3>
            <p className="cal-event-modal__sub">{formatDateLabel(logDate)}</p>
          </div>
          <button type="button" className="cal-event-modal__close" onClick={onClose} aria-label="닫기">
            ×
          </button>
        </div>

        <div className="cal-event-modal__body cal-event-modal__body--gate">
          <section className="gate-log-form__section">
            <div className="gate-log-form__section-head">
              <strong>일시·유형</strong>
              <span>언제, 어떤 기록인지</span>
            </div>
            <div className="gate-log-form__grid gate-log-form__grid--datetime">
              <label>
                <span>날짜 *</span>
                <input type="date" value={logDate} onChange={(e) => setLogDate(e.target.value)} required />
              </label>
              <label>
                <span>시간</span>
                <input type="time" value={logTime} onChange={(e) => setLogTime(e.target.value)} />
              </label>
            </div>
            <div className="gate-log-form__type-pills" role="group" aria-label="유형">
              {GATE_LOG_TYPE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={logType === opt.value ? `is-active is-${opt.value.toLowerCase()}` : undefined}
                  onClick={() => setLogType(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </section>

          <section className="gate-log-form__section">
            <div className="gate-log-form__section-head">
              <strong>{isVisit ? '방문 정보' : isParcel ? '택배 정보' : '기록 내용'}</strong>
              <span>
                {isVisit ? '방문자와 담당자' : isParcel ? '업체·송장·담당' : '요약과 연락처'}
              </span>
            </div>
            <div className="gate-log-form__grid">
              <label className="is-span-2">
                <span>{isVisit ? '방문자명 *' : '내용 요약 *'}</span>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={isVisit ? '홍길동' : '서류 / 부품 박스'}
                  required
                />
              </label>
              <label>
                <span>{isVisit ? '소속' : '업체·택배사'}</span>
                <input
                  value={partyName}
                  onChange={(e) => setPartyName(e.target.value)}
                  placeholder={isVisit ? '○○전자' : 'CJ대한통운'}
                />
              </label>
              <label>
                <span>{isVisit ? '방문자 연락처' : '상대 연락처'}</span>
                <input
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  placeholder="010-0000-0000"
                  inputMode="tel"
                />
              </label>
              <label>
                <span>{isVisit ? '담당자(우리)' : '수령·발송 담당'}</span>
                <input
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  placeholder="담당자명"
                />
              </label>
              {isParcel ? (
                <label>
                  <span>송장번호</span>
                  <input
                    value={trackingNo}
                    onChange={(e) => setTrackingNo(e.target.value)}
                    placeholder="송장번호"
                  />
                </label>
              ) : null}
            </div>
          </section>

          <section className="gate-log-form__section">
            <div className="gate-log-form__section-head">
              <strong>메모</strong>
              <span>목적·비고 (선택)</span>
            </div>
            <div className="gate-log-form__grid">
              <label className="is-span-2">
                <span className="visually-hidden">메모</span>
                <textarea
                  value={memo}
                  onChange={(e) => setMemo(e.target.value)}
                  placeholder="방문 목적, 비고 등"
                  rows={3}
                />
              </label>
            </div>
          </section>
        </div>

        <div className="cal-event-modal__actions">
          {mode === 'edit' ? (
            <button
              type="button"
              className="is-danger"
              onClick={() => void handleDelete()}
              disabled={saving}
            >
              삭제
            </button>
          ) : null}
          <button type="button" className="is-ghost" onClick={onClose}>
            취소
          </button>
          <button type="submit" className="is-primary" disabled={saving}>
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
}

export default GateLogModal;
